// Back into your game after the app (or the tab) was closed by accident.
//
// While you are in a Ranked match, a Royale (its queue included) or a Play Now
// room, the device remembers which (localStorage, so it outlives the app). A
// deliberate exit forgets it: Leave, Play again, the match ending, being
// knocked out of the Royale, or leaving the game's page for another. Closing
// the app or the tab runs none of that, so the game is still remembered, and
// the next start of the app (on the start page) goes straight back into it:
//   Ranked and the Royale  as long as the game can still be in progress
//   Play Now               only within a minute (a drop-in room: after that
//                          a fresh room is as good)
// The server keeps a dropped player's seat (match-runner suspendPlayer) and
// hands it back on a reconnect from the same device. If the game is gone by
// then, the game page lands quietly back on the start page.
export type ResumeKind = "ranked" | "royale" | "playnow";
export interface ResumeEntry { kind: ResumeKind; path: string; room: string; instance: string | null; origin: string | null; at: number }

const KEY = "ar.resume.v1";
const PLAYNOW_MS = 60 * 1000;
const LONGEST_MS = 3 * 60 * 60 * 1000; // nothing runs longer than this: a stale entry is dropped

// Two games can be up at once (Play Now while queued for the Royale): each
// touches only its own entry, and Play Now never takes the place of a Royale
// or a Ranked match that is up too.
const RANK: Record<ResumeKind, number> = { playnow: 0, royale: 1, ranked: 1 };
const up = new Map<ResumeKind, number>(); // the games on screen now
function stored(): ResumeEntry | null {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}
export function gameUp(kind: ResumeKind): void { up.set(kind, (up.get(kind) || 0) + 1); }
export function gameDown(kind: ResumeKind): void { const n = (up.get(kind) || 0) - 1; if (n > 0) up.set(kind, n); else up.delete(kind); }
export function rememberGame(e: ResumeEntry): void {
  for (const k of up.keys()) if (k !== e.kind && RANK[k] > RANK[e.kind]) return;
  try { localStorage.setItem(KEY, JSON.stringify(e)); } catch { /* no storage */ }
}
// Forget the remembered game: `kind`'s only (a deliberate exit from that game),
// or any (no kind).
export function forgetGame(kind?: ResumeKind): void {
  try {
    if (kind) { const e = stored(); if (!e || e.kind !== kind) return; }
    localStorage.removeItem(KEY);
  } catch { /* no storage */ }
}
// The game to go back to, if there is one worth it (read on the app's start).
export function resumableGame(now = Date.now()): ResumeEntry | null {
  const e = stored();
  if (!e || !e.path || !e.kind || typeof e.at !== "number") return null;
  const age = now - e.at;
  if (age < 0 || age > (e.kind === "playnow" ? PLAYNOW_MS : LONGEST_MS)) { forgetGame(); return null; }
  return e;
}

// Set while the app is on its way back into a remembered game: the game page
// then treats a refusal like a reload's (quietly home, no fresh matchmaking)
// and says "Back in your game" once it is in.
let resuming = false;
export function markResuming(): void { resuming = true; }
export function isResuming(): boolean { return resuming; }
export function endResuming(): void { resuming = false; }
